'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var R = require('ramda');
var polished = require('polished');
var PropTypes = _interopDefault(require('prop-types'));
var React = _interopDefault(require('react'));
var emotionTheming = require('emotion-theming');

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

/**
 * Helper function for border-radius.
 * @param {number} number
 * @return {Function} that takes props and returns the value for border-radius
 *         from the theme that corresponds to the level
 */
function borderRadius(number) {
  return R.path(['theme', 'borderRadius', number]);
}

/**
 * Helper function for breakpoints.
 * @param {string} name
 * @return {Function} that takes props and returns the value of the named
 *         breakpoint from the theme
 */
function breakpoint(name) {
  return R.path(['theme', 'breakpoints', name]);
}

/**
 * Helper function for colors.
 * @param {string} name
 * @param {number} opacity
 * @return {Function} that takes props and returns the value of the named
 *         color from the theme
 */
function color(name) {
  var opacity = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

  if (['inherit', 'currentColor'].includes(name)) {
    return R.always(name);
  }

  return function (props) {
    var color = R.path(['theme', 'colors', name], props);
    return color && polished.rgba(color, opacity);
  };
}

/**
 * Helper function for spacings.
 * @param {string} number spacing level (lower level -> smaller spacing)
 * @return {Function} that takes props and returns the spacing value
 *         from the theme that corresponds to the level
 */
function spacing(number) {
  return R.path(['theme', 'paddings', number]);
}

/**
 * Helper function for font sizes.
 * @param {string} number font size level (lower level -> smaller font)
 * @return {Function} that takes props and returns the fontsize
 *         from the theme that corresponds to the level
 */
function fontSize(number) {
  return R.path(['theme', 'fonts', 'sizes', number]);
}

/**
 * Helper function for fonts.
 * @param {string} name
 * @return {Function} that takes props and returns the full name of the named
 *         font from the theme
 */
function font(name) {
  return R.path(['theme', 'fonts', name]);
}

/**
 * Helper function for box shadows.
 * @param {string} number box shadow level (lower level -> smaller shadow)
 * @return {Function} that takes props and returns the box shadow
 *         from the theme that corresponds to the level
 */
function boxShadow(number) {
  return R.path(['theme', 'boxShadow', number]);
}

function render(props) {
  return function (strings, interpolations) {
    var result = '';
    for (var i in strings) {
      var string = strings[i];
      var interpolation = typeof interpolations[i] === 'undefined' ? '' : interpolations[i];

      var part = typeof interpolation === 'function' ? string + interpolation(props) : string + interpolation;

      result = '' + result + part;
    }
    return result;
  };
}

/**
 * Helper function for media queries.
 * @param {string} name `sm`, `md`, or `lg`
 * @return {Function} that takes props and returns the media query from the
 *         theme that corresponds to the name
 */
function mediaQuery(name) {
  var getBreakpoint = breakpoint(name);
  return function (strings) {
    for (var _len = arguments.length, interpolations = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      interpolations[_key - 1] = arguments[_key];
    }

    return function (props) {
      var breakpoint = getBreakpoint(props);
      if (breakpoint) {
        return render(props)(['@media screen and (min-width:', ') {'].concat(toConsumableArray(strings), ['}']), [breakpoint, ''].concat(interpolations, ['']));
      }
    };
  };
}

mediaQuery.xs = mediaQuery('xs');
mediaQuery.sm = mediaQuery('sm');
mediaQuery.md = mediaQuery('md');
mediaQuery.lg = mediaQuery('lg');

font.body = font('body');
font.head = font('head');

var BREAKPOINTS = ['base', 'sm', 'md', 'lg'];

function mergeThemes(props) {
  var base = props.theme || {};
  var sm = R.mergeDeepRight(base, props.overrides.sm || {});
  var md = R.mergeDeepRight(sm, props.overrides.md || {});
  var lg = R.mergeDeepRight(md, props.overrides.lg || {});
  return { base: base, sm: sm, md: md, lg: lg };
}

function getDerivedStateFromProps(props) {
  return mergeThemes(props);
}

function getMediaQuery(name, props) {
  return 'screen and (min-width: ' + breakpoint(name)(props) + ')';
}

function getMediaQueries(props) {
  return R.map(function (name) {
    return getMediaQuery(name, props);
  }, ['sm', 'md', 'lg']);
}

/**
 * Provides relevant theme through React Context API based on media query.
 * @param {Object} props
 * @param {Object} props.children
 * @param {Object} props.overrides
 * @param {Object} props.theme
 * @return {ReactComponent}
 * @example
 * import { ResponsiveThemeProvider } from '@rushplay/common'
 *
 * const theme = {
 *   paddings: ['8px', '16px'],
 *   fonts: {
 *     body: 'Open Sans',
 *     sizes: ['12px', '18px', '30px']
 *   },
 * }
 *
 * const overrides = {
 *   lg: {
 *     fonts: {
 *       sizes: ['14px', '22px', '38px'],
 *     },
 *   },
 * }
 *
 * function Root({ store }) {
 *   return (
 *     <Provider store={store}>
 *       </React.Fragment>
 *         <ResponsiveThemeProvider
 *           theme={theme}
 *           overrides={overrides}>
 *           <Router>
 *             {routes(store)}
 *           </Router>
 *         </ResponsiveThemeProvider>
 *       </React.Fragment>
 *     </Provider>
 *   )
 * }
 */
var ResponsiveThemeProvider = function (_React$Component) {
  inherits(ResponsiveThemeProvider, _React$Component);

  function ResponsiveThemeProvider(props) {
    classCallCheck(this, ResponsiveThemeProvider);

    var _this = possibleConstructorReturn(this, (ResponsiveThemeProvider.__proto__ || Object.getPrototypeOf(ResponsiveThemeProvider)).call(this, props));

    _this.state = getDerivedStateFromProps(props);

    _this.handleChange = _this.handleChange.bind(_this);
    return _this;
  }

  createClass(ResponsiveThemeProvider, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      var _this2 = this;

      var mediaQueries = getMediaQueries(this.props);

      if (window.matchMedia) {
        this.mediaQueries = R.map(function (query) {
          return window.matchMedia(query);
        }, mediaQueries);

        R.forEach(function (query) {
          return query.addListener(_this2.handleChange);
        }, this.mediaQueries);

        this.handleChange();
      }
    }
  }, {
    key: 'componentWillUnmount',
    value: function componentWillUnmount() {
      var _this3 = this;

      R.forEach(function (query) {
        return query.removeListener(_this3.handleChange);
      }, this.mediaQueries);
    }
  }, {
    key: 'handleChange',
    value: function handleChange() {
      var index = R.findLastIndex(function (query) {
        return query.matches;
      }, this.mediaQueries);

      this.setState({ breakpoint: BREAKPOINTS[index] });
    }
  }, {
    key: 'render',
    value: function render() {
      var theme = this.state[this.state.breakpoint || 'base'];
      return React.createElement(
        emotionTheming.ThemeProvider,
        { theme: theme },
        this.props.children
      );
    }
  }]);
  return ResponsiveThemeProvider;
}(React.Component);

ResponsiveThemeProvider.propTypes = {
  children: PropTypes.node,
  overrides: PropTypes.shape({
    sm: PropTypes.object,
    md: PropTypes.object,
    lg: PropTypes.object
  }),
  theme: PropTypes.object.isRequired
};

function y(x) {
  // Amazing formula - No touching!
  return 0.03 * Math.pow(x, 2) + 0.04 * x - 0.05;
}

/**
 * @name createColorSet
 * @desc Create a set of 10 color variations from light to dark based on a
 *       starting color.
 * @param {string} name Name of color used as output prefix
 * @param {string} color Color code in hex format, i.e. '#RRGGBB'
 * @returns {Object} a map of colorName: colorCode pairs
 * @example
 * import { createColorSet } from '@rushplay/common'
 *
 * const blues = createColorSet('blue', '#58B1CB')
 */
function createColorSet(name, color) {
  var brightRatios = R.times(function (n) {
    return y(5 - n);
  }, 5);
  var darkRatios = R.times(function (n) {
    return y(n + 1);
  }, 5);

  var brights = R.map(function (amount) {
    return polished.tint(amount, color);
  }, brightRatios);
  var minDarkRatio = Math.min.apply(Math, darkRatios);
  var darks = R.map(function (amount) {
    return polished.saturate((minDarkRatio - amount) / 4, polished.shade(amount, color));
  }, darkRatios);

  return [].concat(toConsumableArray(brights), [color], toConsumableArray(darks)).reduce(function (set$$1, color, i) {
    return R.assoc(['' + name + i], color, set$$1);
  }, defineProperty({}, name, color));
}

exports.ResponsiveThemeProvider = ResponsiveThemeProvider;
exports.createColorSet = createColorSet;
exports.borderRadius = borderRadius;
exports.breakpoint = breakpoint;
exports.color = color;
exports.spacing = spacing;
exports.fontSize = fontSize;
exports.font = font;
exports.boxShadow = boxShadow;
exports.mediaQuery = mediaQuery;
//# sourceMappingURL=index.js.map
